import { FlowConfig, ValidationResult } from '@/shared/components/FlowContainer/types'
import { TrainingSprintConfig, TrainingPurpose, ActivityType } from './types'

/** Minimum gap in minutes required between the end of a game activity and the next activity. */
export const ACTIVITY_MIN_GAP_MINUTES = 5
import { TrainignSpringDefinePurposeStepOne } from './steps/TrainignSpringDefinePurposeStepOne'

import { TrainingSprintParticipantsStepThree } from './steps/TrainingSprintParticipantsStepThree'
import { ScheduleSessionsStep } from './steps/ScheduleSessionsStep'
import { PreviewAndLaunchStep } from './steps/PreviewAndLaunchStep'
import { TrainingSpringContentStepTwo } from './steps/TrainingSpringContentStepTwo'

const validateDefinePurpose = (_data: TrainingSprintConfig, _stepId: string): ValidationResult => {
  // Always allow next button to be enabled
  return {
    isValid: true,
    errors: [],
    warnings: undefined
  }
}

const validateContentStep = (data: TrainingSprintConfig, _stepId: string): ValidationResult => {
  const errors: string[] = []

  // Reject default "Untitled Training Sprint" name in real-time validation
  if (data.sprintName?.trim() === 'Untitled Training Sprint') {
    errors.push('facilitator.trainingSprint.validation.sprintNameCustomRequired')
  }

  // Sprint name is required
  if (!data.sprintName || data.sprintName.trim() === '') {
    errors.push('facilitator.trainingSprint.validation.sprintNameRequired')
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings: undefined
  }
}

// Separate validation functions for actual validation on proceed
export const validateDefinePurposeOnProceed = (data: TrainingSprintConfig): ValidationResult => {
  const errors: string[] = []
  const warnings: string[] = []

  if (!data.purpose) {
    errors.push('facilitator.trainingSprint.validation.purposeRequired')
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings: warnings.length > 0 ? warnings : undefined
  }
}

export const validateContentStepOnProceed = (data: TrainingSprintConfig): ValidationResult => {
  const errors: string[] = []
  const warnings: string[] = []

  // Sprint name is required for ALL purposes
  if (!data.sprintName || data.sprintName.trim() === '') {
    errors.push('facilitator.trainingSprint.validation.sprintNameRequired')
  }

  // Reject default "Untitled Training Sprint" name
  if (data.sprintName?.trim() === 'Untitled Training Sprint') {
    errors.push('facilitator.trainingSprint.validation.sprintNameCustomRequired')
  }

  if (!data.activities || data.activities.length === 0) {
    errors.push('facilitator.trainingSprint.validation.activitiesRequired')
  }

  // Check for required activities (those with limit 1)
  if (data.activities) {
    const requiredActivities = data.activities.filter((activity) => activity.limit === 1)
    const selectedRequiredActivities = data.activities.filter((activity) =>
      requiredActivities.some((req) => req.type === activity.type)
    )

    for (const requiredActivity of requiredActivities) {
      const count = selectedRequiredActivities.filter((activity) => activity.type === requiredActivity.type).length
      if (count === 0) {
        errors.push(`facilitator.trainingSprint.validation.${requiredActivity.type.toLowerCase()}Required`)
      }
    }
  }

  // An AI coached team debrief needs a game session somewhere before it in the sequence (it debriefs a game).
  if (data.activities) {
    let hasGameBefore = false
    for (const activity of data.activities) {
      if (isGameActivity(activity.type)) hasGameBefore = true
      else if (activity.type === ActivityType.SHORT_REFLECTION && !hasGameBefore) {
        errors.push('facilitator.trainingSprint.validation.aiDebriefRequiresPrecedingGame')
        break
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings: warnings.length > 0 ? warnings : undefined
  }
}

export const validateParticipantsAndTeamsOnProceed = (data: TrainingSprintConfig): ValidationResult => {
  const errors: string[] = []

  if (!data.participants || data.participants.length === 0) {
    errors.push('facilitator.trainingSprint.validation.participantsRequired')
  }
  if (!data.teams || data.teams.length === 0) {
    errors.push('facilitator.trainingSprint.validation.teamsRequired')
  }

  // Check for duplicate emails in participants
  if (data.participants && data.participants.length > 0) {
    const emailCounts = new Map<string, number>()
    const duplicateEmails: string[] = []

    data.participants.forEach((participant) => {
      const email = participant.email.toLowerCase().trim()
      const count = (emailCounts.get(email) || 0) + 1
      emailCounts.set(email, count)

      if (count === 2) {
        // Only add to duplicates list the first time we find a duplicate
        duplicateEmails.push(email)
      }
    })

    if (duplicateEmails.length > 0) {
      errors.push(`facilitator.trainingSprint.validation.duplicateEmails|${duplicateEmails.join(', ')}`)
    }
  }

  // Check for unassigned participants
  if (data.participants && data.teams) {
    const teamParticipantIds = new Set(data.teams.flatMap((team) => team.participants.map((p) => p.id)))
    const unassignedCount = data.participants.filter((p) => !teamParticipantIds.has(p.id)).length
    if (unassignedCount > 0) {
      // Store both the key and the interpolation data as a JSON string for now
      // TODO: Refactor validation to support structured error objects with interpolation data
      errors.push(`facilitator.trainingSprint.validation.unassignedParticipants|${unassignedCount}`)
    }
  }

  // Check for teams with insufficient members (minimum 3)
  if (data.teams) {
    const teamsWithInsufficientMembers = data.teams.filter((team) => team.participants.length < 3)
    if (teamsWithInsufficientMembers.length > 0) {
      errors.push('facilitator.trainingSprint.validation.insufficientTeamMembers')
    }

    const teamsMissingLanguage = data.teams.filter((team) => !team.language?.trim())
    if (teamsMissingLanguage.length > 0) {
      errors.push('facilitator.trainingSprint.validation.teamLanguageRequired')
    }
  }

  return { isValid: errors.length === 0, errors }
}

/**
 * Helper function to convert date and time strings to a comparable Date object
 */
const parseActivityDateTime = (dateString: string, timeString: string): Date => {
  // Date is in ISO format (YYYY-MM-DD) and time is in HH:mm format
  return new Date(`${dateString}T${timeString}:00`)
}

/**
 * Helper function to check if activities are in chronological order
 * Returns indices of activities that are out of order
 */
const findOutOfOrderActivities = (activities: TrainingSprintConfig['activities']): number[] => {
  const outOfOrderIndices: number[] = []

  if (!activities || activities.length <= 1) {
    return outOfOrderIndices
  }

  for (let i = 1; i < activities.length; i++) {
    const prevActivity = activities[i - 1]
    const currentActivity = activities[i]

    // Skip if either activity is not scheduled
    if (
      !prevActivity.scheduledDate ||
      !prevActivity.scheduledTime ||
      !currentActivity.scheduledDate ||
      !currentActivity.scheduledTime
    ) {
      continue
    }

    const prevDateTime = parseActivityDateTime(prevActivity.scheduledDate, prevActivity.scheduledTime)
    const currentDateTime = parseActivityDateTime(currentActivity.scheduledDate, currentActivity.scheduledTime)

    // Current activity must be after previous activity
    if (currentDateTime <= prevDateTime) {
      outOfOrderIndices.push(i)
    }
  }

  return outOfOrderIndices
}

/**
 * Helper function to check if an activity is scheduled in the past
 * @param activity - The activity to check
 * @returns true if the activity's start time is in the past
 */
export const isActivityInPast = (activity: TrainingSprintConfig['activities'][0]): boolean => {
  if (!activity.scheduledDate || !activity.scheduledTime) {
    return false
  }

  const now = new Date()
  const activityDateTime = parseActivityDateTime(activity.scheduledDate, activity.scheduledTime)

  return activityDateTime.getTime() < now.getTime()
}

/**
 * Helper function to find activities that overlap with their predecessor.
 * Two intervals overlap only when they intersect in time (same or overlapping date/time range).
 * An activity entirely before the previous one (e.g. second on March 11, first on March 12) is out of order, not overlap.
 *
 * @param activities - Array of activities to check
 * @returns Array of indices for activities that overlap with the previous activity
 */
export const getOverlappingActivityIndices = (activities: TrainingSprintConfig['activities']): number[] => {
  const overlappingIndices: number[] = []

  if (!activities || activities.length <= 1) {
    return overlappingIndices
  }

  for (let i = 1; i < activities.length; i++) {
    const prevActivity = activities[i - 1]
    const currentActivity = activities[i]

    // Skip if either activity is not fully scheduled
    if (
      !prevActivity.scheduledDate ||
      !prevActivity.scheduledTime ||
      !prevActivity.endTime ||
      !currentActivity.scheduledDate ||
      !currentActivity.scheduledTime ||
      !currentActivity.endTime
    ) {
      continue
    }

    const prevStartTime = parseActivityDateTime(prevActivity.scheduledDate, prevActivity.scheduledTime)
    const prevEndTime = parseActivityDateTime(prevActivity.scheduledDate, prevActivity.endTime)
    const currentStartTime = parseActivityDateTime(currentActivity.scheduledDate, currentActivity.scheduledTime)
    const currentEndTime = parseActivityDateTime(currentActivity.scheduledDate, currentActivity.endTime)

    // Intervals overlap iff: currentStart < prevEnd AND currentEnd > prevStart
    // (so we only flag true time overlap, not "current is entirely before previous")
    const overlaps =
      currentStartTime.getTime() < prevEndTime.getTime() && currentEndTime.getTime() > prevStartTime.getTime()

    if (overlaps) {
      overlappingIndices.push(i)
    }
  }

  return overlappingIndices
}

/**
 * Helper function to check if an activity is a game type
 * @param activityType - The activity type to check
 * @returns true if the activity is a game type
 */
export const isGameActivity = (activityType: ActivityType): boolean => {
  return (
    activityType === ActivityType.FIRST_GAME ||
    activityType === ActivityType.SHORT_GAME ||
    activityType === ActivityType.LONG_GAME
  )
}

/**
 * Helper function to check if an activity doesn't have the required 5-minute break after a game
 * @param prevActivity - Previous activity
 * @param currentActivity - Current activity
 * @returns true if the break requirement is not met, false otherwise
 */
export const isActivityWithoutBreak = (
  prevActivity: TrainingSprintConfig['activities'][0],
  currentActivity: TrainingSprintConfig['activities'][0]
): boolean => {
  // Skip if either activity is not fully scheduled
  if (
    !prevActivity.scheduledDate ||
    !prevActivity.scheduledTime ||
    !prevActivity.endTime ||
    !currentActivity.scheduledDate ||
    !currentActivity.scheduledTime
  ) {
    return false
  }

  // Check if previous activity is a game (break required after ANY game, regardless of what follows)
  if (isGameActivity(prevActivity.type)) {
    const prevEndTime = parseActivityDateTime(prevActivity.scheduledDate, prevActivity.endTime)
    const currentStartTime = parseActivityDateTime(currentActivity.scheduledDate, currentActivity.scheduledTime)

    // Calculate the time difference in minutes
    const timeDifferenceMs = currentStartTime.getTime() - prevEndTime.getTime()
    const timeDifferenceMinutes = timeDifferenceMs / (1000 * 60)

    // Check if there's at least ACTIVITY_MIN_GAP_MINUTES between the end of the game and start of the next activity
    return timeDifferenceMinutes < ACTIVITY_MIN_GAP_MINUTES
  }

  return false
}

/**
 * Helper function to find activities that don't have the required 5-minute break after a game
 * @param activities - Array of activities to check
 * @returns Array of indices for activities that don't have the required break
 */
export const getActivitiesWithoutBreak = (activities: TrainingSprintConfig['activities']): number[] => {
  const withoutBreakIndices: number[] = []

  if (!activities || activities.length <= 1) {
    return withoutBreakIndices
  }

  for (let i = 1; i < activities.length; i++) {
    const prevActivity = activities[i - 1]
    const currentActivity = activities[i]

    if (isActivityWithoutBreak(prevActivity, currentActivity)) {
      withoutBreakIndices.push(i)
    }
  }

  return withoutBreakIndices
}

export const validateScheduleSessionsOnProceed = (data: TrainingSprintConfig): ValidationResult => {
  const errors: string[] = []

  // Check if all activities are scheduled (have scheduledDate, scheduledTime, and endTime)
  if (data.activities && data.activities.length > 0) {
    const unscheduledActivities = data.activities.filter(
      (activity) => !activity.scheduledDate || !activity.scheduledTime || !activity.endTime
    )
    if (unscheduledActivities.length > 0) {
      errors.push('facilitator.trainingSprint.validation.allActivitiesMustBeScheduled')
    }

    // Check chronological order of scheduled activities
    const outOfOrderIndices = findOutOfOrderActivities(data.activities)
    if (outOfOrderIndices.length > 0) {
      errors.push('facilitator.trainingSprint.validation.activitiesNotInChronologicalOrder')
    }

    // Additional validation: ensure no overlaps and no past start times
    const now = new Date()
    const ONE_DAY_MS = 24 * 60 * 60 * 1000
    const scheduled = data.activities
      .filter((a) => a.scheduledDate && a.scheduledTime && a.endTime)
      .map((a) => {
        const start = parseActivityDateTime(a.scheduledDate as string, a.scheduledTime as string)
        let end = parseActivityDateTime(a.scheduledDate as string, a.endTime as string)
        // If end is before or equal to start (e.g. 11:43 PM – 12:43 AM), treat end as next day
        if (end.getTime() <= start.getTime()) {
          end = new Date(end.getTime() + ONE_DAY_MS)
        }
        return { start, end }
      })
      .sort((a, b) => a.start.getTime() - b.start.getTime())

    // Validate each interval
    for (const interval of scheduled) {
      // Start must be before end (should always hold after midnight fix above)
      if (interval.end.getTime() <= interval.start.getTime()) {
        errors.push('facilitator.trainingSprint.validation.activityEndBeforeStart')
        break
      }
      // Start time must not be in the past
      if (interval.start.getTime() < now.getTime()) {
        errors.push('facilitator.trainingSprint.validation.activityStartsInPast')
        break
      }
    }

    // Check overlaps against previous end times
    for (let i = 1; i < scheduled.length; i++) {
      const prev = scheduled[i - 1]
      const curr = scheduled[i]
      // Do not allow overlap: current start must be >= previous end
      if (curr.start.getTime() < prev.end.getTime()) {
        errors.push('facilitator.trainingSprint.validation.activitiesOverlap')
        break
      }
    }

    // Check for required 5-minute break after game sessions
    const withoutBreakIndices = getActivitiesWithoutBreak(data.activities)
    if (withoutBreakIndices.length > 0) {
      errors.push('facilitator.trainingSprint.validation.gameActivityBreakRequired')
    }
  }

  // Check if we have at least one scheduled activity to derive start/end dates
  if (data.activities && data.activities.length > 0) {
    const scheduledActivities = data.activities.filter(
      (activity) => activity.scheduledDate && activity.scheduledTime && activity.endTime
    )

    if (scheduledActivities.length === 0) {
      errors.push('facilitator.trainingSprint.validation.startDateRequired')
    }
  } else {
    errors.push('facilitator.trainingSprint.validation.startDateRequired')
  }

  return { isValid: errors.length === 0, errors }
}

/**
 * Export helper for use in components to check specific activities
 */
export { findOutOfOrderActivities }

export const facilitatorTrainingSprintFlowConfig: FlowConfig<TrainingSprintConfig> = {
  title: 'facilitator.trainingSprint.flow.title',
  description: 'facilitator.trainingSprint.flow.description',
  initialData: {
    purpose: TrainingPurpose.AWARE,
    activities: [],
    participants: [],
    teams: [],
    startDate: '',
    endDate: ''
  },
  steps: [
    {
      id: 'define-purpose',
      title: 'facilitator.trainingSprint.flow.steps.definePurpose',
      component: TrainignSpringDefinePurposeStepOne,
      validation: validateDefinePurpose,
      onProceedValidation: validateDefinePurposeOnProceed,
      rules: {
        required: true
      }
    },
    {
      id: 'content-step',
      title: 'facilitator.trainingSprint.flow.steps.trainingContent',
      component: TrainingSpringContentStepTwo,
      validation: validateContentStep,
      onProceedValidation: validateContentStepOnProceed,
      rules: {
        required: true
      }
    },
    {
      id: 'participants-and-teams',
      title: 'facilitator.trainingSprint.flow.steps.participantsAndTeams',
      component: TrainingSprintParticipantsStepThree,
      validation: (_data: TrainingSprintConfig, _stepId: string): ValidationResult => {
        // Always allow next button to be enabled
        return { isValid: true, errors: [] }
      },
      onProceedValidation: validateParticipantsAndTeamsOnProceed,
      rules: {
        required: true
      }
    },
    {
      id: 'schedule-sessions',
      title: 'facilitator.trainingSprint.flow.steps.scheduleSessions',
      component: ScheduleSessionsStep,
      validation: (_data: TrainingSprintConfig, _stepId: string): ValidationResult => {
        // Always allow next button to be enabled
        return { isValid: true, errors: [] }
      },
      onProceedValidation: validateScheduleSessionsOnProceed,
      rules: {
        required: true
      }
    },
    {
      id: 'preview-launch',
      title: 'facilitator.trainingSprint.flow.steps.previewLaunch',
      component: PreviewAndLaunchStep,
      validation: (_data: TrainingSprintConfig, _stepId: string): ValidationResult => {
        return { isValid: true, errors: [] }
      },
      rules: {
        required: false
      }
    }
  ]
}
